/**
 * The live voice sessions, so sign-out can end them while it can still
 * authorize the call.
 *
 * Sign-out cleared the access token and then navigated away. The pagehide
 * beacon and VoiceControls' unmount both build their `/sessions/{id}/end` call
 * from that token, so both went out without one and were refused: the room
 * kept running (and billing) until the agent's idle terminate or the stale
 * sweep caught it (session-end audit 2026-10-22, item 1a). Each mounted
 * VoiceControls registers its own stop here; `logout` awaits them first.
 */

type Ender = () => Promise<void>;

const enders = new Set<Ender>();

/** Register a mounted session's stop. Returns the unregister. */
export function registerVoiceSession(end: Ender): () => void {
  enders.add(end);
  return () => {
    enders.delete(end);
  };
}

/**
 * End every registered session, waiting at most `capMs` so a slow network
 * never holds sign-out. Returns null when nothing is registered, so a caller
 * can stay synchronous in the common case.
 */
export function endActiveVoiceSessions(capMs = 1500): Promise<void> | null {
  if (enders.size === 0) return null;
  const all = Promise.allSettled([...enders].map((end) => end())).then(() => undefined);
  const cap = new Promise<void>((resolve) => setTimeout(resolve, capMs));
  return Promise.race([all, cap]);
}
